import { TourCaptureIllustration } from '@carboncopyco/illustrations';
import { RecordingTypes, type TourEngineFrame } from '@carboncopyco/types';
import {
  Button,
  Spacer,
  TextBadge,
  TextBase,
  TextBoxEditorTitle,
  TextCaption,
  TextLargeParagraph,
} from '@carboncopyco/ui';
import { AnalyticsTrackEventName, useAnalytics } from '@carboncopyco/utils';
import { useState } from 'react';
import { useTourDispatch, useTourSelector } from '../../hooks/store';
import {
  selectIsCaptureButtonDisabled,
  selectIsCaptureStarted,
  selectIsReadyToCapture,
  selectIsShowingDimensionPanel,
  setIsCapturing,
  setIsCapturingFinished,
} from '../../store';
import { TourCaptureList } from './TourCaptureList';
import { useTourCapturePageProps } from './TourCapturePageContext';

interface TourCaptureSidebarProps {
  frames?: TourEngineFrame[];
  originalTourId?: string;
  demoId: string;
  recordingType: RecordingTypes;
  isOpenStartTourCaptureModal: boolean;
}

export const TourCaptureSidebar = ({
  frames,
  originalTourId,
  demoId,
  recordingType,
  isOpenStartTourCaptureModal,
}: TourCaptureSidebarProps) => {
  const { trackEvent } = useAnalytics();
  const dispatch = useTourDispatch();
  const { uuid, onStartCapturing } = useTourCapturePageProps();
  const isCaptureStarted = useTourSelector(selectIsCaptureStarted(uuid));
  const isReadyToCapture = useTourSelector(selectIsReadyToCapture(uuid));
  const isCaptureButtonDisabled = useTourSelector(
    selectIsCaptureButtonDisabled(uuid)
  );
  const isShowingDimensionPanel = useTourSelector(
    selectIsShowingDimensionPanel(uuid)
  );
  const [isScrollbarVisible, setIsScrollbarVisible] = useState(false);

  const startCapturing = () => {
    originalTourId
      ? trackEvent(AnalyticsTrackEventName.TOUR_CLICK_CAPTURE_NEW_FRAMES, {
          location: 'add_frame_tour_capture',
          demoId,
          demoType:
            recordingType === RecordingTypes.overlay ? 'overlay' : 'clone',
        })
      : trackEvent(AnalyticsTrackEventName.TOUR_CLICK_START_CAPTURE, {
          location: 'tour_capture_drawer',
          demoId,
          demoType:
            recordingType === RecordingTypes.overlay ? 'overlay' : 'clone',
        });
    dispatch(setIsCapturing({ uuid, isCapturing: true }));
    dispatch(
      setIsCapturingFinished({
        uuid,
        isCapturingFinished: false,
      })
    );
    onStartCapturing?.();
  };

  const isCapturingDisabled =
    isOpenStartTourCaptureModal || !isReadyToCapture || isCaptureButtonDisabled;

  const isMac = navigator.userAgent.includes('Macintosh');

  return (
    <div className="flex flex-col h-full border-r border-ds-natural-150 bg-white w-[350px] min-w-[350px] max-w-[350px]">
      {isCaptureStarted && (
        <div className={`py-5 ${isScrollbarVisible && 'shadow-ds-sm'}`}>
          <div className="flex flex-col px-2 -m-1">
            <div className="flex items-center pl-3 py-2">
              <TextBoxEditorTitle
                label="Captured frames"
                uppercase
                color="text-ds-natural-600"
              />
            </div>
          </div>
          <div className="pl-4 pr-6 flex flex-center justify-between">
            <div>
              <TextCaption label="Click through the Tour flow or use shortcut: " />
            </div>
            <div className="bg-ds-natural-500 rounded px-1 h-5 flex items-center mt-1">
              <TextBadge
                ellipsis
                label={isMac ? '⌘⇧S' : '⌃⇧S'}
                color="text-ds-natural-150"
              />
            </div>
          </div>
        </div>
      )}

      {originalTourId && isCaptureStarted && (
        <div className="flex-grow flex justify-between px-3 mb-4">
          <div className="border border-ds-natural-200 bg-ds-natural-50 py-1 px-2 rounded-md flex mt-5= h-14">
            <TextBase
              label="Note: New captured frames will be added into your existing tour"
              color="text-ds-natural-600"
            />
          </div>
        </div>
      )}
      {isCaptureStarted ? (
        <TourCaptureList
          frames={frames}
          onScrollVisibleChange={setIsScrollbarVisible}
        />
      ) : (
        <div className="flex-grow flex flex-col justify-between">
          <div className="flex flex-col text-center justify-center items-center flex-grow px-10">
            <TourCaptureIllustration />
            <Spacer amount={2} />
            <TextLargeParagraph
              label={
                originalTourId
                  ? 'Add frames to an existing tour'
                  : 'Capture your Tour flow'
              }
              color="text-ds-black"
            />
            <Spacer amount={0.5} />
            <div className="px-2 flex flex-col justify-center flex-center items-center">
              <TextBase
                label="Navigate to your first screen."
                color="text-ds-natural-600"
              />
              <TextBase
                label={`When you're ready click "Let's start"`}
                color="text-ds-natural-600"
              />
              <div className="mt-5">
                <Button
                  label="Let's start"
                  onClick={startCapturing}
                  disabled={isCapturingDisabled || isShowingDimensionPanel}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
